import type { IDeviceSettingDef, IDeviceType } from '@/types/equipment.types';

type SettingValue = string | number | boolean;

export const emptyToNull = (value: string): string | null =>
  value.trim() || null;

// Un port illisible vaut « absent » : le serveur refuserait un NaN, et « 0 » n'est pas un port.
export const portToNumber = (value: string): number | null => {
  const port = Number(value);
  return value.trim() !== '' && Number.isInteger(port) && port > 0
    ? port
    : null;
};

const isNumeric = (def: IDeviceSettingDef) =>
  def.kind === 'number' || def.kind === 'integer';

// Les réglages affichés dans le formulaire : la valeur en place, sinon (à la création) le défaut du type.
export const settingsToForm = (
  defs: IDeviceSettingDef[],
  current: Record<string, SettingValue> | null,
  useDefaults: boolean,
): Record<string, string> =>
  Object.fromEntries(
    defs.map((def) => {
      const value = current?.[def.key] ?? (useDefaults ? def.default : null);
      return [
        def.key,
        value === null || value === undefined ? '' : String(value),
      ];
    }),
  );

// Les valeurs saisies vers le corps de la requête : une valeur vide s'efface (null), les nombres et booléens retrouvent leur type.
export const settingsToBody = (
  defs: IDeviceSettingDef[],
  values: Record<string, string>,
): Record<string, SettingValue | null> | null => {
  const entries = defs.flatMap((def): [string, SettingValue | null][] => {
    const raw = (values[def.key] ?? '').trim();
    if (!raw) return [[def.key, null]];
    if (isNumeric(def)) {
      const number = Number(raw);
      return Number.isFinite(number) ? [[def.key, number]] : [[def.key, null]];
    }
    if (def.kind === 'boolean') return [[def.key, raw === 'true']];
    return [[def.key, raw]];
  });
  return entries.some(([, value]) => value !== null)
    ? Object.fromEntries(entries)
    : null;
};

// Changer de type : ce que le nouveau type refuse est effacé, ce qu'il préremplit l'est à son défaut.
export const resetForType = (
  type: IDeviceType | undefined,
  current: { role: string; mac: string; ip: string; port: string },
) => ({
  role: type?.roles.some((r) => r.role === current.role) ? current.role : '',
  mac: type?.mac === 'none' ? '' : current.mac,
  ip: type?.networked ? current.ip : '',
  port: type?.networked && type.port ? String(type.port.default) : '',
  settings: settingsToForm(type?.settings ?? [], null, true),
});

export interface MemberSlot {
  type: string;
  role: string | null;
}

// Par type et par rôle, le premier périphérique est le défaut, les suivants ses replis (1, 2, …) dans l'ordre de la liste.
export const placementOf = (
  members: MemberSlot[],
  index: number,
): { kind: 'default' } | { kind: 'fallback'; number: number } => {
  const target = members[index];
  const before = members
    .slice(0, index)
    .filter((m) => m.type === target.type && m.role === target.role).length;
  return before === 0
    ? { kind: 'default' }
    : { kind: 'fallback', number: before };
};

// Déplace un élément d'un cran sans muter la liste ; hors bornes, la liste revient telle quelle.
export const moveItem = <T>(items: T[], from: number, by: -1 | 1): T[] => {
  const to = from + by;
  if (to < 0 || to >= items.length) return items;
  const next = [...items];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
};
